import React, { useState, useEffect } from "react"
import { useParams, useHistory } from "react-router-dom"
import { Card, CardBody, Row, Col, Button, Modal, ModalBody, ModalFooter, Input, Label, FormGroup } from "reactstrap"
import PanelHeader from "components/PanelHeader/PanelHeader.jsx"
import { useAuth0 } from "react-auth0-spa"
import { useTwilioDevice } from "components/Phone/V1/useTwilioDevice"
import AgentService from "core/api/AgentService"


export default function Dispatch911Report() {
    const { id } = useParams()
    const history = useHistory()
    const { user } = useAuth0()
    const { makeOutgoingCall, updateUserStatusAfterEndCall } = useTwilioDevice()

    const isPendingDispatch = (reason) => reason === "Requested911" || reason === "TimedOut"

    const dispatchDisplay = {
        TimedOut: { title: "Dispatch Timeout", status: "Timed out - No response from company" },
        Requested911: { title: "911 Dispatch Request", status: "Company requested 911 dispatch" },
        Dispatched911: { title: "911 Dispatched", status: "911 has been dispatched" },
        DispatchNotNeeded: { title: "Dispatch Closed", status: "Closed without dispatch" }
    }

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [data, setData] = useState(null)
    const [testMode, setTestMode] = useState(false)

    const [personStillStuck, setPersonStillStuck] = useState(null)
    const [videoCheckNotes, setVideoCheckNotes] = useState("")

    const [call911Checked, setCall911Checked] = useState(false)
    const [showCall911Popup, setShowCall911Popup] = useState(false)
    const [dateTime911Called, setDateTime911Called] = useState(null)
    const [confirmation911, setConfirmation911] = useState(null)
    const [notes911, setNotes911] = useState("")

    const getCalledContacts = (surveyResults) => {
        const calledContacts = []
        if (!surveyResults?.dispatchQuestions) return calledContacts

        const contactQuestions = surveyResults.dispatchQuestions
            .filter(q => q.name?.startsWith('callContact') && !q.name.includes('ElevatorCompany'))

        for (const question of contactQuestions) {
            const questionName = question.name
            const index = questionName.replace('callContact', '')

            if (surveyResults[questionName]?.includes("Yes")) {
                const choiceText = question.choices?.[0]?.text?.replace(/<[^>]*>/g, '') || `Contact ${index}`
                calledContacts.push({
                    contactCalledInformation: choiceText,
                    helpConfirmed: surveyResults[`helpConfirmation${index}`] === "Yes"
                })
            }
        }

        return calledContacts
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await AgentService.getReportData(id)
                const call = response?.call || {}
                const building = response?.building || {}
                const dispatchInfo = call?.dispatchInfo || {}

                setData({
                    callId: call?.id,
                    dispatchReason: dispatchInfo?.status || null,
                    buildingName: building?.buildingName || call?.buildingName,
                    elevatorName: call?.elevatorName,
                    elevatorPhoneNumber: call?.elevatorPhoneNumber,
                    customerName: call?.customerName,
                    videoURL: response?.elevatorVideoURL,
                    videoConnectionInstructions: response?.elevatorVideoConnectionInstructions,
                    outgoingPhoneNumberId: building?.outgoingPhoneNumberId,
                    informationFor911Dispatch: building?.informationFor911Dispatch,
                    reasonForCall: call?.surveyResults?.dispatch,
                    personsName: call?.surveyResults?.personsName,
                    personsNumber: call?.surveyResults?.personsNumber,
                    durationStuck: call?.surveyResults?.durationStuck,
                    locationStuck: call?.surveyResults?.locationStuck,
                    originalAgentNotes: call?.surveyResults?.notes,
                    originalResolution: call?.resolution,
                    originalAgent: call?.userEmail,
                    timeReceived: call?.dateTimeReceived,
                    timeAnswered: call?.dateTimeAnswered,
                    timeSubmitted: call?.dateTimeSubmitted,
                    contactsCalled: getCalledContacts(call?.surveyResults),
                    contactName: dispatchInfo?.contactName,
                    contactPhone: dispatchInfo?.contactPhone,
                    recentCalls: response?.recentCallsSummary?.calls?.map(r => ({ displayText: r?.displayText })) || []
                })
                setTestMode(response?.elevatorOnTestMode ?? false)
            } catch (err) {
                console.error("Failed to load dispatch data:", err)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [id])

    const call911 = () => {
        makeOutgoingCall(testMode ? "933" : "911", user.email, data?.outgoingPhoneNumberId)
        setDateTime911Called(new Date())
        setShowCall911Popup(false)
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            await AgentService.save911DispatchReport({
                callId: data.callId,
                dispatched: call911Checked,
                confirmation911: confirmation911,
                notes: call911Checked ? notes911 : videoCheckNotes,
                dispatchedBy: user.email,
                dateTime911Dispatched: dateTime911Called?.toISOString() || null
            })

            await updateUserStatusAfterEndCall()
            history.goBack()
        } catch (err) {
            console.error("Failed to submit:", err)
            setSubmitting(false)
        }
    }

    const canSubmit = () => {
        if (!isPendingDispatch(data?.dispatchReason)) return false
        if (data?.dispatchReason === "TimedOut" && personStillStuck === null) return false
        if (data?.dispatchReason === "TimedOut" && personStillStuck === "no") return true
        return call911Checked && confirmation911 !== null
    }

    if (loading) {
        return (
            <>
                <PanelHeader size="sm" />
                <div className="content">
                    <p>Loading dispatch information...</p>
                </div>
            </>
        )
    }

    if (!data) {
        return (
            <>
                <PanelHeader size="sm" />
                <div className="content">
                    <p>Dispatch data not found.</p>
                    <Button color="primary" onClick={() => history.goBack()}>Go Back</Button>
                </div>
            </>
        )
    }

    return (
        <>
            <PanelHeader content={
                <div className="header text-center">
                    <h1 className="title">
                        {dispatchDisplay[data.dispatchReason]?.title || "Call Details"}
                    </h1>
                    <h2 className="title">
                        {data.elevatorName} - {data.buildingName}
                    </h2>
                </div>
            } />

            <div className="content">
                <Row>
                    <Col md="12">
                        <Card>
                            <CardBody>
                                <h5 style={{ marginBottom: "15px" }}><strong>Dispatch Information</strong></h5>
                                <p><strong>Status:</strong> {dispatchDisplay[data.dispatchReason]?.status || "No dispatch needed"}</p>
                                {data.dispatchReason && data.dispatchReason !== "TimedOut" && data.contactName && <p><strong>Contact:</strong> {data.contactName} ({data.contactPhone})</p>}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <h5 style={{ marginBottom: "15px" }}><strong>Call Summary</strong></h5>
                                <Row>
                                    <Col xs="12" md="6">
                                        <p><strong>Customer:</strong> {data.customerName}</p>
                                        <p><strong>Building:</strong> {data.buildingName}</p>
                                        <p><strong>Elevator:</strong> {data.elevatorName}</p>
                                        <p><strong>Elevator Phone:</strong> {data.elevatorPhoneNumber}</p>
                                        <p><strong>Time Received:</strong> {new Date(data.timeReceived).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                                        <p><strong>Time Answered:</strong> {new Date(data.timeAnswered).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                                        <p><strong>Time Submitted:</strong> {new Date(data.timeSubmitted).toLocaleString([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                                        <p><strong>Original Agent:</strong> {data.originalAgent}</p>
                                        <p><strong>Resolution:</strong> {data.originalResolution}</p>
                                    </Col>
                                    <Col xs="12" md="6">
                                        <p><strong>Reason for Call:</strong> {data.reasonForCall}</p>
                                        <p><strong>Passenger Name:</strong> {data.personsName}</p>
                                        <p><strong>Passenger Phone:</strong> {data.personsNumber}</p>
                                        {data.durationStuck && <p><strong>Time Stuck:</strong> {data.durationStuck}</p>}
                                        {data.locationStuck && <p><strong>Floor:</strong> {data.locationStuck}</p>}
                                        {data.contactsCalled?.length > 0 && (
                                            <>
                                                <p style={{ marginBottom: "5px" }}><strong>Contacts Called:</strong></p>
                                                <div style={{ paddingLeft: "15px" }}>
                                                    {data.contactsCalled.map((contact, i) => (
                                                        <p key={i} style={{ marginBottom: "3px" }}>{contact.contactCalledInformation} ({contact.helpConfirmed ? "Help confirmed" : "No confirmation"})</p>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {data.originalAgentNotes && <p style={{ marginTop: "10px" }}><strong>Agent Notes:</strong> {data.originalAgentNotes}</p>}
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>


                        {/* Leave out recent calls for now */}
                        {/* {data.recentCalls?.length > 0 && (
                            <Card>
                                <CardBody>
                                    <h5 style={{ marginBottom: "15px" }}><strong>Recent Calls</strong></h5>
                                    {data.recentCalls.map((call, i) => (
                                        <div key={i} style={{ padding: "10px", marginBottom: "8px", background: "#f8f9fa", borderLeft: "3px solid #ffc107", borderRadius: "3px" }}>
                                            {call.displayText}
                                        </div>
                                    ))}
                                </CardBody>
                            </Card>
                        )} */}

                        {data.dispatchReason === "TimedOut" && (
                            <Card>
                                <CardBody>
                                    <h5 style={{ marginBottom: "20px" }}><strong>Video Check</strong></h5>
                                    {data.videoURL ? (
                                        <div style={{ marginBottom: "20px" }}>
                                            <p><strong>Video:</strong> <a href={data.videoURL} target="_blank" rel="noopener noreferrer">Click here to view video</a></p>
                                            {data.videoConnectionInstructions && <p><strong>Instructions:</strong> {data.videoConnectionInstructions}</p>}
                                        </div>
                                    ) : (
                                        <p style={{ marginBottom: "20px" }}>No video available for this elevator.</p>
                                    )}
                                    <div style={{ marginTop: "40px", marginBottom: "10px" }}>
                                        <p style={{ marginBottom: "12px", fontWeight: "bold", color: "#333" }}>Is the person still stuck in the elevator?</p>
                                        <div style={{ display: "flex", gap: "30px" }}>
                                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#333", fontSize: "16px" }}>
                                                <Input type="radio" name="stillStuck" onChange={() => setPersonStillStuck("yes")} checked={personStillStuck === "yes"} style={{ marginRight: "8px" }} />
                                                Yes
                                            </label>
                                            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#333", fontSize: "16px" }}>
                                                <Input type="radio" name="stillStuck" checked={personStillStuck === "no"} style={{ marginRight: "8px" }}
                                                    onChange={() => {
                                                        setPersonStillStuck("no");
                                                        setDateTime911Called(null);
                                                        setCall911Checked(false);
                                                        setConfirmation911(null);
                                                    }}
                                                />
                                                No
                                            </label>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {data.dispatchReason === "TimedOut" && personStillStuck === "no" && (
                            <Card>
                                <CardBody>
                                    <h5 style={{ marginBottom: "15px" }}><strong>No Dispatch Needed</strong></h5>
                                    <p>Video check confirmed person is not stuck. You can close this dispatch request.</p>
                                    <FormGroup style={{ marginTop: "15px" }}>
                                        <Label style={{ fontWeight: "bold", color: "#333" }}>Notes (optional):</Label>
                                        <Input type="textarea" value={videoCheckNotes} onChange={(e) => setVideoCheckNotes(e.target.value)} rows={3} />
                                    </FormGroup>
                                </CardBody>
                            </Card>
                        )}

                        {isPendingDispatch(data.dispatchReason) && (data.dispatchReason !== "TimedOut" || personStillStuck === "yes") && (
                            <Card>
                                <CardBody>
                                    <h5 style={{ marginBottom: "20px" }}><strong>Dispatch {testMode ? "933" : "911"}</strong></h5>

                                    {data.informationFor911Dispatch && (
                                        <div style={{ marginBottom: "20px" }}>
                                            <p style={{ marginBottom: "8px" }}><strong>Information for {testMode ? "933" : "911"}:</strong></p>
                                            <p style={{ whiteSpace: "pre-line" }}>{data.informationFor911Dispatch}</p>
                                        </div>
                                    )}

                                    <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "20px", minHeight: "32px" }}>
                                        <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "16px", fontWeight: "bold", color: "#333", margin: 0 }}>
                                            <Input type="checkbox" checked={call911Checked} style={{ marginRight: "10px", marginTop: 0, marginBottom: 0, position: "relative", top: "-1px" }}
                                                onChange={(e) => {
                                                    setCall911Checked(e.target.checked);
                                                    if (!e.target.checked) {
                                                        setDateTime911Called(null);
                                                        setConfirmation911(null);
                                                    }
                                                }} />
                                            Call {testMode ? "933" : "911"}
                                        </label>
                                        {call911Checked && (
                                            <div style={{ minWidth: "150px", display: "flex", alignItems: "center" }}>
                                                {!dateTime911Called ? (
                                                    <Button 
                                                        color="primary"
                                                        style={{
                                                            padding: "0 14px",
                                                            fontSize: "14px",
                                                            height: "32px",
                                                            minWidth: "auto",
                                                            lineHeight: "30px"
                                                        }}
                                                        onClick={() => setShowCall911Popup(true)}
                                                    >
                                                        Call {testMode ? "933" : "911"}
                                                    </Button>
                                                ) : (
                                                        <span style={{ fontStyle: "italic", fontWeight: "bold", fontSize: "16px" }}>
                                                            Called at {dateTime911Called.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                        </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {call911Checked && (
                                        <div style={{ paddingLeft: "10px" }}>
                                            <div style={{ marginBottom: "25px" }}>
                                                <p style={{ marginBottom: "12px", fontWeight: "bold", color: "#333" }}>Did you get confirmation that help is on the way?</p>
                                                <div style={{ display: "flex", gap: "30px" }}>
                                                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#333", fontSize: "16px" }}>
                                                        <Input type="radio" name="confirmation911" onChange={() => setConfirmation911(true)} checked={confirmation911 === true} style={{ marginRight: "8px" }} />
                                                        Yes
                                                    </label>
                                                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", color: "#333", fontSize: "16px" }}>
                                                        <Input type="radio" name="confirmation911" onChange={() => setConfirmation911(false)} checked={confirmation911 === false} style={{ marginRight: "8px" }} />
                                                        No
                                                    </label>
                                                </div>
                                            </div>

                                            <FormGroup style={{ marginBottom: 0 }}>
                                                <Label style={{ fontWeight: "bold", color: "#333" }}>{testMode ? "933" : "911"} Notes (optional):</Label>
                                                <Input type="textarea" value={notes911} onChange={(e) => setNotes911(e.target.value)} rows={3} />
                                            </FormGroup>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {isPendingDispatch(data?.dispatchReason) && (data?.dispatchReason !== "TimedOut" || personStillStuck !== null) && (

                            <Button color="primary" disabled={!canSubmit() || submitting} onClick={handleSubmit}>
                                {submitting ? "Submitting..." : (data?.dispatchReason === "TimedOut" && personStillStuck === "no") ? "Close Without Dispatch" : "Submit Report"}
                            </Button>

                        )}
                    </Col>
                </Row>
            </div>

            <Modal isOpen={showCall911Popup} centered={true}>
                <ModalBody style={{ justifyContent: "center", alignContent: "center", textAlign: "center" }}>
                    <div className="title" style={{ fontSize: 18 }}>Call {testMode ? "933" : "911"}</div>
                    <i className="las la-phone" style={{ fontSize: 40 }}></i>
                    <p style={{ fontSize: 16 }}>Are you sure you want to call {testMode ? "933" : "911"}?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="success" onClick={call911} style={{ margin: "auto", display: "block" }}>Call</Button>
                    <Button color="danger" onClick={() => setShowCall911Popup(false)} style={{ margin: "auto", display: "block" }}>Cancel</Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
